import * as Sentry from '@sentry/react';
import { isApolloError } from '@apollo/client';
import { toast } from 'sonner';

type LoggerContext = Record<string, unknown>;

const reportedErrors = new WeakSet<object>();
const NULLABILITY_ERROR = 'Cannot return null for non-nullable';

export const ERROR_TOAST_ID = 'api-error';

const isObject = (value: unknown): value is object =>
  (typeof value === 'object' && value !== null) || typeof value === 'function';

export const isNullabilityError = (error: unknown) => {
  if (error instanceof Error && isApolloError(error) && error.graphQLErrors.length) {
    return error.graphQLErrors.every((item) =>
      item.message.includes(NULLABILITY_ERROR),
    );
  }

  return (
    typeof (error as { message?: unknown })?.message === 'string' &&
    (error as { message: string }).message.includes(NULLABILITY_ERROR)
  );
};

const shouldReport = (error: unknown) => {
  // Apollo errors are already reported in the Apollo errorLink.
  if (error instanceof Error && isApolloError(error)) return false;
  if (isNullabilityError(error)) return false;

  if (!isObject(error)) return true;
  if (reportedErrors.has(error)) return false;
  reportedErrors.add(error);
  return true;
};

export const logger = {
  error: (error: unknown, context?: LoggerContext) => {
    if (!shouldReport(error)) return;
    Sentry.captureException(error, context ? { extra: context } : undefined);
  },
  info: (message: string, context?: LoggerContext) => {
    Sentry.captureMessage(message, {
      level: 'info',
      ...(context ? { extra: context } : {}),
    });
  },
};

export const showErrorToast = (message?: string, error?: unknown) => {
  if (isNullabilityError(error)) return;
  toast.error(message, { id: ERROR_TOAST_ID });
};
